import * as zod from 'zod';

const IndexedNumberObject = zod
  .record(zod.number())
  .transform((object): number[] => Object.values(object));

const NumberArrayOrIndexedNumberObject = zod.union([
  zod.array(zod.number()),
  IndexedNumberObject,
]);

export const StdMsgsTime = zod.object({
  sec: zod.number(),
  nanosec: zod.number(),
});

export type StdMsgsTime = zod.infer<typeof StdMsgsTime>;

export const StdMsgsHeader = zod.object({
  stamp: StdMsgsTime,
  frame_id: zod.string(),
});

export type StdMsgsHeader = zod.infer<typeof StdMsgsHeader>;

export const TrjMsgsJointTrajectoryPoint = zod.object({
  positions: NumberArrayOrIndexedNumberObject,
  velocities: NumberArrayOrIndexedNumberObject,
  accelerations: NumberArrayOrIndexedNumberObject,
  effort: NumberArrayOrIndexedNumberObject,
  time_from_start: StdMsgsTime,
});

export type TrjMsgsJointTrajectoryPoint = zod.infer<
  typeof TrjMsgsJointTrajectoryPoint
>;

export const TrjMsgsJointTrajectory = zod.object({
  header: StdMsgsHeader,
  joint_names: zod.array(zod.string()),
  points: zod.array(TrjMsgsJointTrajectoryPoint),
});

export const MotionProfileRange = zod.object({
  // Authored waypoint index (matches the target index in routine-runner).
  profile_index: zod.number(),
  // Index of the trajectory (within the result `trajectories` array) that this
  // waypoint lands in.
  resampled_trajectory_index: zod.number(),
  // First/last point index, within that trajectory's `points`, spanned by this
  // waypoint after timing + resampling. `resampled_end_index` is where the
  // waypoint is considered reached.
  resampled_start_index: zod.number(),
  resampled_end_index: zod.number(),
});

export type MotionProfileRange = zod.infer<typeof MotionProfileRange>;

export const TaskFromRobotGoalActionResult = zod.object({
  success: zod.boolean(),
  trajectories: zod.array(TrjMsgsJointTrajectory),
  // Optional so legacy cache entries and served/simulated plans that omit it
  // still parse. See makeRosMotionPlanCacheBackend round-trip.
  motion_profile_ranges: zod.array(MotionProfileRange).optional().default([]),
});

export type TaskFromRobotGoalActionResult = zod.infer<
  typeof TaskFromRobotGoalActionResult
>;
